import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiMessageSquare,
  FiXCircle,
} from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { extractList } from '../utilities/tournamentsApi';
import { getPlayerPhotoUrl, getTeamPhotoUrl } from '../utilities/playersApi';
import {
  buildAcceptPayload,
  capApplicationId,
  copyTeamImage,
  copyTeamName,
  extractCapTicket,
  formatCapDate,
  formatTeamNames,
  isCapApplicationFromBot,
  isCapPlayerProfessional,
  isPlayerAccepted,
  isPlayerDeclined,
  nextPlayerStatus,
  numWord,
  playerActionType,
  playerFullName,
  playerPositionLabel,
  playerWarningKey,
  resetPlayerStatus,
  somePlayersDecided,
} from '../utilities/applicationsApi';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/applicationsManagement.css';

const statusClass = (status) => {
  switch (Number(status)) {
    case 1: return 'accepted';
    case 0: return 'declined';
    case 3: return 'partial';
    case 2: return 'pending';
    default: return 'pending';
  }
};

export default function ManageApplication() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { refreshPendingCapApplications } = useChampionships();

  const [application, setApplication] = useState(null);
  const [players, setPlayers] = useState([]);
  const [copies, setCopies] = useState([]);
  const [currentApplication, setCurrentApplication] = useState([]);
  const [openPlayers, setOpenPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const [commentModal, setCommentModal] = useState({ visible: false, playerId: null, value: '' });
  const [declineVisible, setDeclineVisible] = useState(false);
  const [declineComment, setDeclineComment] = useState('');
  const [acceptVisible, setAcceptVisible] = useState(false);
  const [teamsModal, setTeamsModal] = useState({ visible: false, player: null });
  const [playerApps, setPlayerApps] = useState([]);
  const [playerAppsLoading, setPlayerAppsLoading] = useState(false);

  useEffect(() => {
    loadApplication(true);
  }, [id]);

  const loadApplication = async (showLoader = false, { keepMessage = false } = {}) => {
    if (showLoader) setLoading(true);
    if (!keepMessage) setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCapApplication, { id: Number(id) });
      const ticket = extractCapTicket(response.data);
      setApplication(ticket.application || null);
      setPlayers(ticket.players);
      setCopies(ticket.copies);
      setCurrentApplication(ticket.currentApplication);
    } catch (error) {
      if (showLoader) {
        setApplication(null);
        setPlayers([]);
        setCopies([]);
        setCurrentApplication([]);
      }
      setMessage({ text: getApiErrorMessage(error, t('capLoadError')), type: 'error' });
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const showFeedback = (text, type) => {
    setMessage({ text, type });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ticketId = capApplicationId(application);
  const dateLocale = lang === 'en' ? 'en-GB' : 'ru-RU';

  const applicationStatusLabel = (status) => {
    switch (Number(status)) {
      case 1: return t('capStatusAccepted');
      case 0: return t('capStatusDeclined');
      case 3: return t('capStatusPartial');
      case 2: return t('capStatusPending');
      default: return '—';
    }
  };

  const playerStatusLabel = (status) => {
    if (isPlayerAccepted(status)) return t('capPlayerStatusAccepted');
    if (isPlayerDeclined(status)) return t('capPlayerStatusDeclined');
    return t('capPlayerStatusPending');
  };

  const actionLabel = (status) => {
    const type = playerActionType(status);
    if (type === 'remove') return t('capActionRemove');
    if (type === 'change') return t('capActionChange');
    return t('capActionAdd');
  };

  const warningText = (key) => {
    if (key === 'blacklist') return t('capBlacklist');
    if (key === 'sameNumber') return t('capWarnSameNumber');
    if (key === 'numberTaken') return t('capWarnNumberTaken');
    if (key === 'alreadyIn') return t('capWarnAlreadyIn');
    return '';
  };

  const changePlayerStatus = (playerId, oldStatus, action) => {
    const nextStatus = nextPlayerStatus(oldStatus, action);
    setPlayers((prev) => prev.map((player) => (
      player.id === playerId ? { ...player, status: nextStatus } : player
    )));
  };

  const approveAllPlayers = () => {
    const decided = somePlayersDecided(players);
    setPlayers((prev) => prev.map((player) => ({
      ...player,
      status: decided ? resetPlayerStatus(player.status) : nextPlayerStatus(player.status, 'accept'),
    })));
  };

  const togglePlayer = (playerId) => {
    setOpenPlayers((prev) => (
      prev.includes(playerId) ? prev.filter((value) => value !== playerId) : prev.concat(playerId)
    ));
  };

  const collapsePlayers = () => {
    if (openPlayers.length) {
      setOpenPlayers([]);
      return;
    }
    setOpenPlayers(players.map((player) => player.id));
  };

  const openPlayerTeams = async (player) => {
    if (!player?.player_id) return;
    setTeamsModal({ visible: true, player });
    setPlayerApps([]);
    setPlayerAppsLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminPlayerApplications, { id: player.player_id });
      setPlayerApps(extractList(response.data));
    } catch (error) {
      setPlayerApps([]);
      setMessage({ text: getApiErrorMessage(error, t('capPlayerAppsError')), type: 'error' });
    } finally {
      setPlayerAppsLoading(false);
    }
  };

  const addComment = () => {
    setPlayers((prev) => prev.map((player) => (
      player.id === commentModal.playerId
        ? { ...player, admin_comment: commentModal.value }
        : player
    )));
    setCommentModal({ visible: false, playerId: null, value: '' });
  };

  const acceptApplication = async () => {
    if (!ticketId || saving) return;
    setSaving(true);
    setAcceptVisible(false);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.acceptCapApplication, {
        id: ticketId,
        application_data: buildAcceptPayload(application, players, copies),
      });
      await refreshPendingCapApplications();
      await loadApplication(false, { keepMessage: true });
      showFeedback(t('capAcceptSuccess'), 'success');
    } catch (error) {
      showFeedback(getApiErrorMessage(error, t('capAcceptError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const declineApplication = async () => {
    if (!ticketId || saving) return;
    setSaving(true);
    setDeclineVisible(false);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.declineCapApplication, {
        id: ticketId,
        application_data: {
          admin_comment: declineComment,
          team_id: application.team_id,
          team_name: application.team_name,
          created_id: application.created_id,
        },
      });
      setDeclineComment('');
      await refreshPendingCapApplications();
      await loadApplication(false, { keepMessage: true });
      showFeedback(t('capDeclineSuccess'), 'success');
    } catch (error) {
      showFeedback(getApiErrorMessage(error, t('capDeclineError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = players.filter((player) => [0, 3, 6].includes(Number(player.status))).length;
  const acceptedCount = players.filter((player) => isPlayerAccepted(player.status)).length;
  const declinedCount = players.filter((player) => isPlayerDeclined(player.status)).length;
  const relatedWord = numWord(copies.length, [t('capRelatedOne'), t('capRelatedFew'), t('capRelatedMany')]);
  const decidedAny = somePlayersDecided(players);
  const forgotDecisions = players.length > 0 && !decidedAny && Number(application?.status) === 2;

  const renderFeedback = () => {
    if (!message.text) return null;
    const Icon = message.type === 'success' ? FiCheckCircle : FiAlertCircle;
    return (
      <div className={`st-message cap-feedback ${message.type}`} role="status">
        <Icon />
        <span>{message.text}</span>
      </div>
    );
  };

  const rostersPath = (application?.team_id && application?.application_id)
    ? `/team_rosters/${application.team_id}/${application.application_id}`
    : '';

  const renderPlayerApps = (player) => {
    const count = Number(player.teams_count) || 0;
    if (!count) {
      return (
        <div className="cap-mini">
          <span className="cap-mini-label">{t('capPlayerApps')}</span>
          <span>{t('capNoActiveApps')}</span>
        </div>
      );
    }
    return (
      <div className="cap-mini">
        <span className="cap-mini-label">{t('capPlayerApps')}</span>
        <span>{formatTeamNames(player.first_three_teams) || '—'}</span>
        {count > 3 ? <span>{t('capMoreApps', { count: count - 3 })}</span> : null}
        {player.player_id ? (
          <button type="button" className="cap-link" onClick={() => openPlayerTeams(player)}>
            {t('capMore')}
          </button>
        ) : null}
      </div>
    );
  };

  const renderPlayers = () => {
    if (!players.length) {
      return <p className="st-hint">{t('capNoPlayers')}</p>;
    }

    return players.map((player) => {
      const warning = playerWarningKey(player, players, currentApplication);
      const opened = openPlayers.includes(player.id);
      const blacklisted = warning === 'blacklist';
      return (
        <div
          key={player.id}
          className={`cap-player ${blacklisted ? 'is-disabled' : ''} ${playerActionType(player.status)}`}
        >
          {warning ? (
            <div className={`cap-warning ${blacklisted ? 'is-error' : ''}`}>
              {blacklisted ? <FiAlertCircle /> : <FiAlertTriangle />}
              <span>{warningText(warning)}</span>
            </div>
          ) : null}
          <div className="cap-player-main">
            <span className={`cap-action cap-action-${playerActionType(player.status)}`}>
              {actionLabel(player.status)}
            </span>
            <img
              src={getPlayerPhotoUrl(player.image) || defaultPlayerImage}
              alt=""
              className="cap-player-photo"
              onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
            />
            <div className="cap-player-identity">
              <div className="cap-player-name-row">
                {player.player_id ? (
                  <Link to={`/players_management/${player.player_id}`} className="cap-player-name">
                    {playerFullName(player) || '—'}
                  </Link>
                ) : (
                  <span className="cap-player-name">{playerFullName(player) || '—'}</span>
                )}
                {isCapPlayerProfessional(player) ? (
                  <span className="cap-pro-badge" title={t('plStatusPro')}>{t('plStatusPro')}</span>
                ) : null}
              </div>
              <span className="cap-player-meta">{t('capNumber')}: {player.player_number || '—'}</span>
            </div>
            <span className={`st-badge cap-badge ${isPlayerAccepted(player.status) ? 'accepted' : isPlayerDeclined(player.status) ? 'declined' : 'pending'}`}>
              {playerStatusLabel(player.status)}
            </span>
            <button type="button" className={`cap-expand ${opened ? 'is-open' : ''}`} onClick={() => togglePlayer(player.id)}>
              <FiChevronDown />
            </button>
            <div className="cap-player-actions">
              <button
                type="button"
                className={`cap-icon-btn is-accept ${isPlayerAccepted(player.status) ? 'is-on' : ''}`}
                onClick={() => changePlayerStatus(player.id, player.status, 'accept')}
                disabled={blacklisted}
              >
                <FiCheckCircle />
              </button>
              <button
                type="button"
                className={`cap-icon-btn is-decline ${isPlayerDeclined(player.status) ? 'is-on' : ''}`}
                onClick={() => changePlayerStatus(player.id, player.status, 'decline')}
                disabled={blacklisted}
              >
                <FiXCircle />
              </button>
              <button
                type="button"
                className={`cap-icon-btn is-comment ${player.admin_comment ? 'is-on' : ''}`}
                onClick={() => setCommentModal({
                  visible: true,
                  playerId: player.id,
                  value: player.admin_comment || '',
                })}
              >
                <FiMessageSquare />
              </button>
            </div>
          </div>
          {opened ? (
            <div className="cap-player-details">
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capHeight')}</span>
                <span>{player.height || '—'}</span>
              </div>
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capWeight')}</span>
                <span>{player.weight || '—'}</span>
              </div>
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capBirthday')}</span>
                <span>
                  {player.birthday
                    ? new Date(player.birthday).toLocaleDateString(dateLocale)
                    : '—'}
                </span>
              </div>
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capNationality')}</span>
                <span>{player.nationality || '—'}</span>
              </div>
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capFoot')}</span>
                <span>{player.work_foot || '—'}</span>
              </div>
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capPosition')}</span>
                <span>{playerPositionLabel(player) || '—'}</span>
              </div>
              <div className="cap-mini">
                <span className="cap-mini-label">{t('capAdminComment')}</span>
                <span>{player.admin_comment || '—'}</span>
              </div>
              {renderPlayerApps(player)}
            </div>
          ) : null}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('capLoadingOne')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!application || !ticketId) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={[
                { label: t('navApplications'), to: '/applications_managment' },
                { label: t('capNotFound') },
              ]} />
            </div>
          </div>
          {message.text ? renderFeedback() : null}
        </div>
      </div>
    );
  }

  const captainName = [application.cap_family, application.cap_name].filter(Boolean).join(' ').trim();

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[
              { label: t('navApplications'), to: '/applications_managment' },
              { label: t('capManageTitle', { id: ticketId }) },
            ]} />
          </div>
        </div>

        {renderFeedback()}

        <div className="st-scroll">
          <div className="st-content">
            {forgotDecisions ? (
              <div className="cap-notice is-warn" role="status">
                <FiAlertTriangle />
                <span>{t('capForgotDecisions')}</span>
              </div>
            ) : null}
            <div className="st-block">
              <h2 className="st-block-header">{t('capInfo')}</h2>
              <div className="cap-info">
                <div className="cap-team">
                  <img
                    src={getTeamPhotoUrl(application.team_image) || defaultTeamLogo}
                    alt=""
                    className="cap-team-logo"
                    onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                  />
                  <div>
                    <div className="cap-team-title">
                      <Link
                        to={`/teams_management/${application.team_id}`}
                        className="cap-team-name"
                      >
                        {application.team_name || '—'}
                      </Link>
                      {isCapApplicationFromBot(application) ? (
                        <span className="cap-source-badge" title={t('capFromBot')}>{t('capFromBot')}</span>
                      ) : null}
                    </div>
                    <div className="cap-captain">{t('capCaptain')}: {captainName || '—'}</div>
                    {copies.length ? (
                      <div className="cap-copies">
                        <span>{t('capRelated', { count: copies.length, word: relatedWord })}</span>
                        <div className="cap-copy-list">
                          {copies.map((copy) => (
                            <span key={copy.team_id || copy.id} className="cap-copy">
                              <img
                                src={getTeamPhotoUrl(copyTeamImage(copy)) || defaultTeamLogo}
                                alt=""
                                onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                              />
                              {copyTeamName(copy)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="cap-mini">
                  <span className="cap-mini-label">{t('capName')}</span>
                  <span>{application.application_name || '—'}</span>
                </div>
                <div className="cap-mini">
                  <span className="cap-mini-label">{t('capStatus')}</span>
                  <span className={`st-badge cap-badge ${statusClass(application.status)}`}>
                    {applicationStatusLabel(application.status)}
                  </span>
                </div>
                <div className="cap-mini">
                  <span className="cap-mini-label">{t('capDate')}</span>
                  <span>{formatCapDate(application.created_at, lang)}</span>
                </div>
              </div>
            </div>

            <div className="st-block">
              <h2 className="st-block-header">{t('capComments')}</h2>
              <div className="st-form-grid">
                <div className="st-field">
                  <label>{t('capCapComment')}</label>
                  <div className="cap-comment">{application.cap_comment || '—'}</div>
                </div>
                <div className="st-field">
                  <label>{t('capAdminComment')}</label>
                  <div className="cap-comment">{application.admin_comment || '—'}</div>
                </div>
              </div>
            </div>

            <div className="st-block">
              <h2 className="st-block-header">{t('capCounts')}</h2>
              <div className="cap-stats">
                <div className="cap-stat">
                  <strong>{players.length}</strong>
                  <span>{t('capTotal')}</span>
                </div>
                <div className="cap-stat is-accepted">
                  <strong>{acceptedCount}</strong>
                  <span>{t('capApproved')}</span>
                </div>
                <div className="cap-stat is-declined">
                  <strong>{declinedCount}</strong>
                  <span>{t('capRejected')}</span>
                </div>
                <div className="cap-stat is-pending">
                  <strong>{pendingCount}</strong>
                  <span>{t('capWaiting')}</span>
                </div>
              </div>
            </div>

            <div className="st-block">
              <div className="cap-players-header">
                <h2 className="st-block-header">{t('capPlayers')}</h2>
                <div className="cap-players-tools">
                  <button type="button" className="st-btn st-btn-ghost" onClick={collapsePlayers}>
                    {openPlayers.length ? t('capCollapseAll') : t('capExpandAll')}
                  </button>
                  <button type="button" className="st-btn st-btn-ghost" onClick={approveAllPlayers}>
                    {somePlayersDecided(players) ? t('capResetAll') : t('capApproveAll')}
                  </button>
                </div>
              </div>
              {renderPlayers()}
            </div>
          </div>
        </div>

        <div className="st-actions st-actions-split">
          {rostersPath ? (
            <Link
              to={rostersPath}
              className="st-btn st-btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('capCurrentRoster', { count: currentApplication.length })}
            </Link>
          ) : (
            <button type="button" className="st-btn st-btn-secondary" disabled>
              {t('capCurrentRoster', { count: currentApplication.length })}
            </button>
          )}
          <div className="st-actions-group">
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving}
              onClick={() => setDeclineVisible(true)}
            >
              {t('capDecline')}
            </button>
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={saving}
              onClick={() => setAcceptVisible(true)}
            >
              {saving ? t('capSaving') : t('capAccept')}
            </button>
          </div>
        </div>
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={commentModal.visible}
        closeModal={() => setCommentModal({ visible: false, playerId: null, value: '' })}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('capCommentTitle')}</div>
          <textarea
            className="st-textarea"
            value={commentModal.value}
            placeholder={t('capCommentPh')}
            onChange={(event) => setCommentModal((prev) => ({ ...prev, value: event.target.value }))}
          />
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              onClick={() => setCommentModal({ visible: false, playerId: null, value: '' })}
            >
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-primary-modal" onClick={addComment}>
              {t('capAddComment')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 560, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={teamsModal.visible}
        closeModal={() => setTeamsModal({ visible: false, player: null })}
      >
        <div className="st-delete-modal cap-teams-modal">
          <div className="customModalHeader">
            {t('capPlayerTeamsTitle', { name: playerFullName(teamsModal.player || {}) })}
          </div>
          <div className="cap-teams-head">
            <span>{t('capTeam')}</span>
            <span>{t('capName')}</span>
            <span>{t('capLeague')}</span>
            <span>{t('capNumber')}</span>
          </div>
          {playerAppsLoading ? (
            <div className="st-loading">
              <Loader type="Oval" color="#3ad1a0" height={32} width={32} />
            </div>
          ) : playerApps.length ? (
            playerApps.map((item, index) => (
              <div key={`${item.team_id || item.id || index}-${item.application_id || index}`} className="cap-teams-row">
                <span className="tr-name">
                  <img
                    src={getTeamPhotoUrl(item.image) || defaultTeamLogo}
                    alt=""
                    className="tr-team-logo"
                    onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                  />
                  <span>{item.team_name || '—'}</span>
                </span>
                <span>{item.application_name || '—'}</span>
                <span>{item.league_name || '—'}</span>
                <span>{item.player_number || '—'}</span>
              </div>
            ))
          ) : (
            <p>{t('capPlayerAppsEmpty')}</p>
          )}
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={declineVisible}
        closeModal={() => setDeclineVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('capDeclineTitle')}</div>
          <p>{t('capDeclineText')}</p>
          <p>{t('capDeclineText2')}</p>
          <textarea
            className="st-textarea"
            value={declineComment}
            placeholder={t('capDeclinePh')}
            onChange={(event) => setDeclineComment(event.target.value)}
          />
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeclineVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={saving} onClick={declineApplication}>
              {t('capContinue')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={acceptVisible}
        closeModal={() => setAcceptVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('capAcceptTitle')}</div>
          {!decidedAny && players.length ? (
            <p className="cap-modal-warn">{t('capAcceptEmptyWarn')}</p>
          ) : null}
          <p>
            {t('capAcceptText', {
              decided: players.length - pendingCount,
              total: players.length,
            })}
          </p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setAcceptVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-primary-modal" disabled={saving} onClick={acceptApplication}>
              {t('capContinue')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
